import React, { useState, useContext, useEffect, useRef, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Input from 'components/Input'
import Textarea from 'components/Textarea'
import Checkbox from 'components/Checkbox'
import Tooltip from 'components/Tooltip'
import MultiSelectionDropdown from 'components/multiSelectDropdown'
import ecoSystem from 'constants/ecoSystem'
import formatNum2Dp from 'utils/formatNum2DP'
import formatNum4Dp from 'utils/formatNum4DP'
import InfoIcon from 'assets/icons/info-icon'
import { CountriesContext } from 'providers/countries'
import { SiteContext } from 'providers/site'
import { MySiteContext } from 'providers/mySiteProvider'
import { delayCancellable } from 'utils/delay'
import { minMaxValidation } from 'utils/minBestMaxValidation'
import ScrollToTop from 'components/ScrollToTop'
import AssessmentTitle from 'components/AssessmentTitle'
import Habitats from './habitats/components/habitats'
import { ProfileContext } from 'providers/profile'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { useSiteEditPermissions } from '../../hooks/useSiteEditPermissions'
import { useBoundaryGeometry } from '../../hooks/useBoundaryGeometry'
import { useDelayedSiteUpdate } from '../../hooks/useDelayedSiteUpdate'
import { buildDetailsInitialState } from './utils/detailsInitialState'
import { registerPendingSiteSaveFlush } from '../../utils/pendingSiteSaveFlushes'
import { AssessmentPageSubTittleIconWrapper } from '../assessment/style'
import {
  handleCalculatedCoverageChange,
  handleRangeFieldChange,
  toSavedNumber,
  hasAlternativeCoverageValue,
  isNumericInputAllowed,
  hasProtectedOrConservedCoverage
} from './utils/numericFieldHandlers'

const minMaxFields = {
  min: 'minimumDepth',
  max: 'maximumDepth'
}

const minMaxAltFields = {
  min: 'lowestAltitude',
  max: 'highestAltitude'
}

const inputSaveDelayTime = 300
const updateSiteNameTimeout = delayCancellable(inputSaveDelayTime)
const updateSiteNameNationalTimeout = delayCancellable(inputSaveDelayTime)
const updateStateChange = delayCancellable(inputSaveDelayTime)
const updateSiteAreaTimeout = delayCancellable(inputSaveDelayTime)
const updateLatitudeOfMidPointTimeout = delayCancellable(inputSaveDelayTime)
const updateLongitudeOfMidPointTimeout = delayCancellable(inputSaveDelayTime)
const updateRationaleForSiteInformationTimeout = delayCancellable(inputSaveDelayTime)
const updateSiteDescriptionTimeout = delayCancellable(inputSaveDelayTime)
const sensitiveSiteDetailsDebounce = delayCancellable(inputSaveDelayTime)
const updateHowIsTheSiteManagedTimeout = delayCancellable(inputSaveDelayTime)
const updateDeliniationRationaleTimeout = delayCancellable(inputSaveDelayTime)
const updateBoundarySourceTimeout = delayCancellable(inputSaveDelayTime)
const updateBiodiversityValuesTimeout = delayCancellable(inputSaveDelayTime)
const updateLandUseRegimesAtSiteTimeout = delayCancellable(inputSaveDelayTime)
const updateNotesTimeout = delayCancellable(inputSaveDelayTime)
const updateIndigenousGroupsTimeout = delayCancellable(inputSaveDelayTime)
const updateThreatsDescriptionValuesTimeout = delayCancellable(inputSaveDelayTime)
const updateMaximumDepthValuesTimeout = delayCancellable(inputSaveDelayTime)
const updateSupersededValuesTimeout = delayCancellable(inputSaveDelayTime)
const updateCoveredByPaValuesTimeout = delayCancellable(inputSaveDelayTime)
const updateCoveredByOECMValuesTimeout = delayCancellable(inputSaveDelayTime)
const updateCoveredByWhatProtectedAreasTimeout = delayCancellable(inputSaveDelayTime)
const updatePaUserUpdatedTimeout = delayCancellable(inputSaveDelayTime)
const updatePaUserUpdatedJustificationTimeout = delayCancellable(inputSaveDelayTime)
const updateOecmUserUpdatedTimeout = delayCancellable(inputSaveDelayTime)
const updateOecmUserUpdatedJustificationTimeout = delayCancellable(inputSaveDelayTime)
const updateHabitatTimeout = delayCancellable(inputSaveDelayTime)
const updateReferencessTimeout = delayCancellable(inputSaveDelayTime)
const contributorsSaveDebounce = delayCancellable(inputSaveDelayTime)

const detailsSaveDebouncers = [
  updateSiteNameTimeout,
  updateSiteNameNationalTimeout,
  updateStateChange,
  updateSiteAreaTimeout,
  updateLatitudeOfMidPointTimeout,
  updateLongitudeOfMidPointTimeout,
  updateRationaleForSiteInformationTimeout,
  updateSiteDescriptionTimeout,
  sensitiveSiteDetailsDebounce,
  updateHowIsTheSiteManagedTimeout,
  updateDeliniationRationaleTimeout,
  updateBoundarySourceTimeout,
  updateBiodiversityValuesTimeout,
  updateLandUseRegimesAtSiteTimeout,
  updateNotesTimeout,
  updateIndigenousGroupsTimeout,
  updateThreatsDescriptionValuesTimeout,
  updateMaximumDepthValuesTimeout,
  updateSupersededValuesTimeout,
  updateCoveredByPaValuesTimeout,
  updateCoveredByOECMValuesTimeout,
  updateCoveredByWhatProtectedAreasTimeout,
  updatePaUserUpdatedTimeout,
  updatePaUserUpdatedJustificationTimeout,
  updateOecmUserUpdatedTimeout,
  updateOecmUserUpdatedJustificationTimeout,
  updateHabitatTimeout,
  updateReferencessTimeout,
  contributorsSaveDebounce
]

const flushAllDetailsSaves = () => {
  return Promise.all(
    detailsSaveDebouncers.map((debouncer) => {
      const result = debouncer.flush()
      if (result && typeof result.then === 'function') {
        return result
      }
      return Promise.resolve()
    })
  )
}

const asSaveTimeout = (debouncer) => (callback) => debouncer.schedule(callback)

const tooltipXOffset = 0
const tooltipYOffset = 16

const getRequiredFieldError = (isValid, field) => {
  if (isValid) {
    return { field: '', error: '' }
  }

  return {
    field,
    error: 'pleaseCompleteThisField'
  }
}

// altitudeError only lives as long as this page is mounted, so fall back to a range error derived
// from the current values. That keeps the warning visible after navigating away and back.
const getAltitudeFieldError = (isValid, altitudeError, rangeError, field) => {
  const activeError = altitudeError.field ? altitudeError : rangeError

  if (activeError.field === field) {
    return activeError
  }

  if (!isValid) {
    return getRequiredFieldError(false, field)
  }

  return { field: '', error: '' }
}

// The full-precision value is what gets saved; the field shows it rounded once the user leaves it.
const formatOnBlur = (setValue, formatter) => (e) => {
  const value = e.target.value
  if (value === '' || Number.isNaN(Number(value))) {
    return
  }
  setValue(formatter(value))
}

// Numeric fields are displayed with a fixed number of decimals. Every keystroke flows back into
// SiteContext, so re-applying that formatting here would rewrite the text the user is still typing
// ("2" -> "2.00", "12.3456" -> "12.35"). Compare against the stored value rather than the formatted
// one, so only a genuine change from elsewhere (Map edit, tab return) replaces what is on screen.
const syncNumericField = (setValue, nextValue, storedValue) => {
  setValue((currentValue) => {
    if (currentValue === '' || nextValue === '') {
      return nextValue
    }
    return Number(currentValue) === Number(storedValue) ? currentValue : nextValue
  })
}

const getDepthFieldError = (depthError, field) => {
  if (depthError.field === field) {
    return depthError
  }

  return { field: '', error: '' }
}

const DetailsPage = () => {
  const { site, setSite, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const { refreshSiteCounts, refreshSites } = useContext(MySiteContext)
  const { countriesOptions } = useContext(CountriesContext)
  const params = useParams()
  const siteData = site || {}

  const { disabled, editOnlySiteStatus } = useSiteEditPermissions({
    site: siteData,
    isEditable,
    roleId: profile?.RoleId
  })

  // PA/OECM overrides and their narratives are editable in Draft and Edit Only only, and
  // only when the user may actually edit — `editOnlySiteStatus` is also true for Submitted
  // Edits (10) and for non-proposer roles on Edit Only (9), both of which are read-only.
  const canEditPaOecmOverrides =
    (editOnlySiteStatus || siteData.SiteStatusId === SITE_STATUS_DRAFT_ID) && !disabled

  const historyId = editOnlySiteStatus ? siteData.historyId : params.siteHistoryId
  const isReassessment = Boolean(params.siteHistoryId)

  const { saveSiteData } = useDelayedSiteUpdate({ site: siteData, params, historyId })

  useBoundaryGeometry({ site: siteData, params, historyId, isReassessment })

  const initialState = buildDetailsInitialState(siteData)

  const [name, setName] = useState(initialState.name)
  const [siteNameNational, setSiteNameNational] = useState(initialState.siteNameNational)
  const [CountryCode, setCountryCode] = useState(initialState.countryCode)
  const [state, setState] = useState(initialState.state)
  const [siteArea, setSiteArea] = useState(initialState.siteArea)
  const [latitudeOfMidPoint, setLatitudeOfMidPoint] = useState(initialState.latitudeOfMidPoint)
  const [longitudeOfMidPoint, setLongitudeOfMidPoint] = useState(initialState.longitudeOfMidPoint)
  const [highestAltitude, setHighestAltitude] = useState(initialState.highestAltitude)
  const [lowestAltitude, setLowestAltitude] = useState(initialState.lowestAltitude)
  const [minimumDepth, setMinimumDepth] = useState(initialState.minimumDepth)
  const [maximumDepth, setMaximumDepth] = useState(initialState.maximumDepth)
  const [depthError, setDepthError] = useState({ field: '', error: '' })
  const [altitudeError, setAltitudeError] = useState({ field: '', error: '' })
  const [selectedSystems, setSelectedSystems] = useState(initialState.selectedSystems)
  const [rationaleForSiteInformation, setRationaleForSiteInformation] = useState(initialState.rationaleForSiteInformation)
  const [siteDescription, setSiteDescription] = useState(initialState.siteDescription)
  const [sensitiveSite, setSensitiveSite] = useState(initialState.sensitiveSite)
  const [sensitiveSiteDetails, setSensitiveSiteDetails] = useState(initialState.sensitiveSiteDetails)
  // An altitude rejected for being out of range stays on screen but is never sent to the server.
  // Remember it so it can be saved once the other bound makes the pair valid again.
  const unsavedAltitudesRef = useRef({})
  const sensitiveSiteRef = useRef(sensitiveSite)
  sensitiveSiteRef.current = sensitiveSite
  const [howIsTheSiteManaged, setHowIsTheSiteManaged] = useState(initialState.howIsTheSiteManaged)
  const [deliniationRationale, setDeliniationRationale] = useState(initialState.deliniationRationale)
  const [boundarySource, setBoundarySource] = useState(initialState.boundarySource)
  const [additionalBiodiversityValues, setAdditionalBiodiversityValues] = useState(initialState.additionalBiodiversityValues)
  const [landUseRegimesAtSite, setLandUseRegimesAtSite] = useState(initialState.landUseRegimesAtSite)
  const [coveredByWhatProtectedAreas, setCoveredByWhatProtectedAreas] = useState(initialState.coveredByWhatProtectedAreas)
  const [coveredByPa, setCoveredByPa] = useState(initialState.coveredByPa)
  const [coveredByOECM, setCoveredByOECM] = useState(initialState.coveredByOECM)
  const [paUserUpdated, setPaUserUpdated] = useState(initialState.paUserUpdated)
  const [paUserUpdatedJustification, setPaUserUpdatedJustification] = useState(initialState.paUserUpdatedJustification)
  const [oecmUserUpdated, setOecmUserUpdated] = useState(initialState.oecmUserUpdated)
  const [oecmUserUpdatedJustification, setOecmUserUpdatedJustification] = useState(initialState.oecmUserUpdatedJustification)
  const [threatsDescription, setThreatsDescription] = useState(initialState.threatsDescription)
  const [supersidedSites, setSupersidedSites] = useState(initialState.supersidedSites)
  const [notes, setNotes] = useState(initialState.notes)
  const [habitat, setHabitat] = useState(initialState.habitat)
  const [indigenousGroups, setIndigenousGroups] = useState(initialState.indigenousGroups)
  const [references, setReferences] = useState(initialState.references)
  const [contributors, setContributors] = useState(initialState.contributors)
  const [countryInput, setCountryInput] = useState('')

  const updateDetailsFieldLocally = useCallback(
    (partial) => {
      if (typeof setSite !== 'function') {
        return
      }
      setSite((current) => ({ ...current, ...partial }))
    },
    [setSite]
  )

  // Keep local Details fields in sync when SiteContext updates (e.g. after tab leave/return or Map edits).
  useEffect(() => {
    const next = buildDetailsInitialState(siteData)
    setName(next.name)
    setSiteNameNational(next.siteNameNational)
    setCountryCode(next.countryCode)
    setState(next.state)
    syncNumericField(setSiteArea, next.siteArea, siteData.siteArea)
    syncNumericField(setLatitudeOfMidPoint, next.latitudeOfMidPoint, siteData.latitudeOfMidPoint)
    syncNumericField(setLongitudeOfMidPoint, next.longitudeOfMidPoint, siteData.longitudeOfMidPoint)
    syncNumericField(setHighestAltitude, next.highestAltitude, siteData.highestAltitude)
    syncNumericField(setLowestAltitude, next.lowestAltitude, siteData.lowestAltitude)
    syncNumericField(setMinimumDepth, next.minimumDepth, siteData.minDepth)
    syncNumericField(setMaximumDepth, next.maximumDepth, siteData.maxDepth)
    setSelectedSystems(next.selectedSystems)
    setRationaleForSiteInformation(next.rationaleForSiteInformation)
    setSiteDescription(next.siteDescription)
    setSensitiveSite(next.sensitiveSite)
    setSensitiveSiteDetails(next.sensitiveSiteDetails)
    setHowIsTheSiteManaged(next.howIsTheSiteManaged)
    setDeliniationRationale(next.deliniationRationale)
    setBoundarySource(next.boundarySource)
    setAdditionalBiodiversityValues(next.additionalBiodiversityValues)
    setLandUseRegimesAtSite(next.landUseRegimesAtSite)
    setCoveredByWhatProtectedAreas(next.coveredByWhatProtectedAreas)
    syncNumericField(setCoveredByPa, next.coveredByPa, siteData.calculatedProtectedArea)
    syncNumericField(setCoveredByOECM, next.coveredByOECM, siteData.calculatedOECMArea)
    syncNumericField(setPaUserUpdated, next.paUserUpdated, siteData.pa_user_updated)
    setPaUserUpdatedJustification(next.paUserUpdatedJustification)
    syncNumericField(setOecmUserUpdated, next.oecmUserUpdated, siteData.oecm_user_updated)
    setOecmUserUpdatedJustification(next.oecmUserUpdatedJustification)
    setThreatsDescription(next.threatsDescription)
    setSupersidedSites(next.supersidedSites)
    setNotes(next.notes)
    setHabitat(next.habitat)
    setIndigenousGroups(next.indigenousGroups)
    setReferences(next.references)
    setContributors(next.contributors)
  }, [
    siteData.name,
    siteData.siteNameNational,
    siteData.SiteDetailsCountryId,
    siteData.state,
    siteData.siteArea,
    siteData.latitudeOfMidPoint,
    siteData.longitudeOfMidPoint,
    siteData.highestAltitude,
    siteData.lowestAltitude,
    siteData.minDepth,
    siteData.maxDepth,
    siteData.terrestrial_system,
    siteData.marine_system,
    siteData.freshwater_system,
    siteData.subterranean_system,
    siteData.rationaleForSiteInformation,
    siteData.siteDescription,
    siteData.sensitive_site,
    siteData.sensitiveSite,
    siteData.sensitive_site_details,
    siteData.sensitiveSiteDetails,
    siteData.howIsTheSiteManaged,
    siteData.deliniationRationale,
    siteData.boundary_source,
    siteData.additionalBiodiversityValues,
    siteData.landUseRegimesAtSite,
    siteData.coveredByWhatProtectedAreas,
    siteData.calculatedProtectedArea,
    siteData.calculatedOECMArea,
    siteData.pa_user_updated,
    siteData.pa_user_updated_justification,
    siteData.oecm_user_updated,
    siteData.oecm_user_updated_justification,
    siteData.threatsDescription,
    siteData.supersededSitesDesription,
    siteData.notes,
    siteData.habitatDescription,
    siteData.indigenousGroups,
    siteData.references,
    siteData.consultationWithContributors
  ])

  useEffect(() => {
    const unregister = registerPendingSiteSaveFlush(flushAllDetailsSaves)
    return () => {
      flushAllDetailsSaves()
      unregister()
    }
  }, [])

  if (!siteData.id) {
    return null
  }

  const altitudeRangeError = minMaxValidation(lowestAltitude, highestAltitude, minMaxAltFields, 'max')

  const handleSearchCountry = (e) => {
    setCountryInput(e)
  }

  const handleNameChange = async (e) => {
    const value = e.target.value
    setName(value)
    updateDetailsFieldLocally({ name: value })
    updateSiteNameTimeout.schedule(() => saveSiteData({ name: value }))
  }

  const handleIndigenousGroupsChange = async (e) => {
    const value = e.target.value
    setIndigenousGroups(value)
    updateDetailsFieldLocally({ indigenousGroups: value })
    updateIndigenousGroupsTimeout.schedule(() => saveSiteData({ indigenousGroups: value }))
  }

  const handleSiteNationalNameChange = async (e) => {
    const value = e.target.value
    setSiteNameNational(value)
    updateDetailsFieldLocally({ siteNameNational: value })
    updateSiteNameNationalTimeout.schedule(() => saveSiteData({ siteNameNational: value }))
  }

  const handleCountryChange = async (e) => {
    setCountryCode(e)
    updateDetailsFieldLocally({ SiteDetailsCountryId: e })
    await saveSiteData({ SiteDetailsCountryId: e })
  }

  const handleStateChange = async (e) => {
    const value = e.target.value
    setState(value)
    updateDetailsFieldLocally({ state: value })
    updateStateChange.schedule(() => saveSiteData({ state: value }))
  }

  const handleAreaChange = async (e) => {
    const value = e.target.value
    if (isNumericInputAllowed(value)) {
      setSiteArea(value)
      updateDetailsFieldLocally({ siteArea: value === '' ? null : value })
    }
    updateSiteAreaTimeout.schedule(() =>
      saveSiteData({ siteArea: value === '' ? null : Number(value).toFixed(10) })
    )
  }

  const handleLatitudeChange = async (e) => {
    const value = e.target.value
    if (isNumericInputAllowed(value)) {
      setLatitudeOfMidPoint(value)
      updateDetailsFieldLocally({ latitudeOfMidPoint: value === '' ? null : value })
    }
    updateLatitudeOfMidPointTimeout.schedule(() =>
      saveSiteData({ latitudeOfMidPoint: value === '' ? null : Number(value).toFixed(10) })
    )
  }

  const handleLongitudeChange = async (e) => {
    const value = e.target.value
    if (isNumericInputAllowed(value)) {
      setLongitudeOfMidPoint(value)
      updateDetailsFieldLocally({ longitudeOfMidPoint: value === '' ? null : value })
    }
    updateLongitudeOfMidPointTimeout.schedule(() =>
      saveSiteData({ longitudeOfMidPoint: value === '' ? null : Number(value).toFixed(10) })
    )
  }

  // Keeps the stored pair in step with what is on screen: a value the server refused earlier is
  // sent as soon as an edit to the other bound makes the range valid.
  const reconcileAltitudeSaves = async (field, value, saved) => {
    if (!saved) {
      unsavedAltitudesRef.current[field] = value
      return
    }

    delete unsavedAltitudesRef.current[field]

    const counterpart = field === minMaxAltFields.min ? minMaxAltFields.max : minMaxAltFields.min
    const pendingValue = unsavedAltitudesRef.current[counterpart]

    if (pendingValue === undefined) {
      return
    }

    delete unsavedAltitudesRef.current[counterpart]
    await saveSiteData({ [counterpart]: toSavedNumber(pendingValue) })
  }

  const handleHighestAltitudeChange = async (e) => {
    const value = e.target.value
    const { saved } = await handleRangeFieldChange({
      value,
      firstValue: lowestAltitude,
      secondValue: value,
      validationFields: minMaxAltFields,
      validationBoundary: 'max',
      setValue: (nextValue) => {
        setHighestAltitude(nextValue)
        updateDetailsFieldLocally({ highestAltitude: nextValue === '' ? null : nextValue })
      },
      setError: setAltitudeError,
      saveSiteData,
      saveFieldName: 'highestAltitude',
      requireNoZeroOnValidRange: false
    })
    await reconcileAltitudeSaves('highestAltitude', value, saved)
  }

  const handleLowestAltitudeChange = async (e) => {
    const value = e.target.value
    const { saved } = await handleRangeFieldChange({
      value,
      firstValue: value,
      secondValue: highestAltitude,
      validationFields: minMaxAltFields,
      validationBoundary: 'min',
      setValue: (nextValue) => {
        setLowestAltitude(nextValue)
        updateDetailsFieldLocally({ lowestAltitude: nextValue === '' ? null : nextValue })
      },
      setError: setAltitudeError,
      saveSiteData,
      saveFieldName: 'lowestAltitude'
    })
    await reconcileAltitudeSaves('lowestAltitude', value, saved)
  }

  const handleMinimumDepthChange = async (e) => {
    await handleRangeFieldChange({
      value: e.target.value,
      firstValue: e.target.value,
      secondValue: maximumDepth,
      validationFields: minMaxFields,
      validationBoundary: 'min',
      setValue: (value) => {
        setMinimumDepth(value)
        updateDetailsFieldLocally({ minDepth: value === '' ? null : value })
      },
      setError: setDepthError,
      saveSiteData,
      saveFieldName: 'minDepth'
    })
  }

  const handleMaximumDepthChange = async (e) => {
    await handleRangeFieldChange({
      value: e.target.value,
      firstValue: minimumDepth,
      secondValue: e.target.value,
      validationFields: minMaxFields,
      validationBoundary: 'max',
      setValue: (value) => {
        setMaximumDepth(value)
        updateDetailsFieldLocally({ maxDepth: value === '' ? null : value })
      },
      setError: setDepthError,
      saveSiteData,
      saveFieldName: 'maxDepth',
      saveWithTimeout: asSaveTimeout(updateMaximumDepthValuesTimeout)
    })
  }

  const handleSystemsChange = async (selectedValues) => {
    setSelectedSystems(selectedValues)
    const systemsPayload = {
      terrestrial_system: selectedValues.includes(1),
      marine_system: selectedValues.includes(2),
      freshwater_system: selectedValues.includes(3),
      subterranean_system: selectedValues.includes(4)
    }
    updateDetailsFieldLocally(systemsPayload)
    await saveSiteData(systemsPayload)
  }

  const handleRationaleChange = async (e) => {
    const value = e.target.value
    setRationaleForSiteInformation(value)
    updateDetailsFieldLocally({ rationaleForSiteInformation: value })
    updateRationaleForSiteInformationTimeout.schedule(() =>
      saveSiteData({ rationaleForSiteInformation: value })
    )
  }

  const handleDescriptionChange = async (e) => {
    const value = e.target.value
    setSiteDescription(value)
    updateDetailsFieldLocally({ siteDescription: value })
    updateSiteDescriptionTimeout.schedule(() => saveSiteData({ siteDescription: value }))
  }

  const handleSensitiveSiteToggle = async () => {
    if (disabled) {
      return
    }
    sensitiveSiteDetailsDebounce.cancel()
    const newValue = !sensitiveSite
    setSensitiveSite(newValue)
    if (!newValue) {
      setSensitiveSiteDetails('')
    }

    const payload = {
      sensitive_site: newValue,
      sensitive_site_details: newValue ? (sensitiveSiteDetails?.trim() || null) : null
    }
    updateDetailsFieldLocally(payload)
    await saveSiteData(payload)

    if (refreshSiteCounts) refreshSiteCounts()
    if (refreshSites) refreshSites()
  }

  const handleSensitiveSiteDetailsChange = async (e) => {
    const value = e.target.value
    setSensitiveSiteDetails(value)
    updateDetailsFieldLocally({
      sensitive_site: sensitiveSiteRef.current,
      sensitive_site_details: value?.trim() || null
    })

    sensitiveSiteDetailsDebounce.schedule(() =>
      saveSiteData({
        sensitive_site: sensitiveSiteRef.current,
        sensitive_site_details: value?.trim() || null
      })
    )
  }

  const handleManagedChange = async (e) => {
    const value = e.target.value
    setHowIsTheSiteManaged(value)
    updateDetailsFieldLocally({ howIsTheSiteManaged: value })
    updateHowIsTheSiteManagedTimeout.schedule(() => saveSiteData({ howIsTheSiteManaged: value }))
  }

  const handleSupersidedSitesChange = async (e) => {
    const value = e.target.value
    setSupersidedSites(value)
    updateDetailsFieldLocally({ supersededSitesDesription: value })
    updateSupersededValuesTimeout.schedule(() => saveSiteData({ supersededSitesDesription: value }))
  }

  const handleDelineationChange = async (e) => {
    const value = e.target.value
    setDeliniationRationale(value)
    updateDetailsFieldLocally({ deliniationRationale: value })
    updateDeliniationRationaleTimeout.schedule(() => saveSiteData({ deliniationRationale: value }))
  }

  const handleBoundarySourceChange = async (e) => {
    const value = e.target.value
    setBoundarySource(value)
    updateDetailsFieldLocally({ boundary_source: value })
    updateBoundarySourceTimeout.schedule(() => saveSiteData({ boundary_source: value }))
  }

  const handleBiodiversityValuesChange = async (e) => {
    const value = e.target.value
    setAdditionalBiodiversityValues(value)
    updateDetailsFieldLocally({ additionalBiodiversityValues: value })
    updateBiodiversityValuesTimeout.schedule(() => saveSiteData({ additionalBiodiversityValues: value }))
  }

  const handleLandUseChange = async (e) => {
    const value = e.target.value
    setLandUseRegimesAtSite(value)
    updateDetailsFieldLocally({ landUseRegimesAtSite: value })
    updateLandUseRegimesAtSiteTimeout.schedule(() => saveSiteData({ landUseRegimesAtSite: value }))
  }

  const handleCoveredByPaChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: (value) => {
        setCoveredByPa(value)
        updateDetailsFieldLocally({ calculatedProtectedArea: value === '' ? null : value })
      },
      saveSiteData,
      saveFieldName: 'calculatedProtectedArea',
      saveWithTimeout: asSaveTimeout(updateCoveredByPaValuesTimeout)
    })
  }

  const handleCoveredByOECMChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: (value) => {
        setCoveredByOECM(value)
        updateDetailsFieldLocally({ calculatedOECMArea: value === '' ? null : value })
      },
      saveSiteData,
      saveFieldName: 'calculatedOECMArea',
      saveWithTimeout: asSaveTimeout(updateCoveredByOECMValuesTimeout)
    })
  }

  const handleCoveredByWhatProtectedAreasChange = async (e) => {
    const value = e.target.value
    setCoveredByWhatProtectedAreas(value)
    updateDetailsFieldLocally({ coveredByWhatProtectedAreas: value })
    updateCoveredByWhatProtectedAreasTimeout.schedule(() =>
      saveSiteData({ coveredByWhatProtectedAreas: value })
    )
  }

  const handlePaUserUpdatedChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: (value) => {
        setPaUserUpdated(value)
        updateDetailsFieldLocally({ pa_user_updated: value === '' ? null : value })
      },
      saveSiteData,
      saveFieldName: 'pa_user_updated',
      saveWithTimeout: asSaveTimeout(updatePaUserUpdatedTimeout)
    })
  }

  const handlePaUserUpdatedJustificationChange = async (e) => {
    const value = e.target.value
    setPaUserUpdatedJustification(value)
    updateDetailsFieldLocally({ pa_user_updated_justification: value })
    updatePaUserUpdatedJustificationTimeout.schedule(() =>
      saveSiteData({ pa_user_updated_justification: value })
    )
  }

  const handleOecmUserUpdatedChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: (value) => {
        setOecmUserUpdated(value)
        updateDetailsFieldLocally({ oecm_user_updated: value === '' ? null : value })
      },
      saveSiteData,
      saveFieldName: 'oecm_user_updated',
      saveWithTimeout: asSaveTimeout(updateOecmUserUpdatedTimeout)
    })
  }

  const handleOecmUserUpdatedJustificationChange = async (e) => {
    const value = e.target.value
    setOecmUserUpdatedJustification(value)
    updateDetailsFieldLocally({ oecm_user_updated_justification: value })
    updateOecmUserUpdatedJustificationTimeout.schedule(() =>
      saveSiteData({ oecm_user_updated_justification: value })
    )
  }

  const handleThreatsDescriptionChange = async (e) => {
    const value = e.target.value
    setThreatsDescription(value)
    updateDetailsFieldLocally({ threatsDescription: value })
    updateThreatsDescriptionValuesTimeout.schedule(() => saveSiteData({ threatsDescription: value }))
  }

  const handleNotesChange = async (e) => {
    const value = e.target.value
    setNotes(value)
    updateDetailsFieldLocally({ notes: value })
    updateNotesTimeout.schedule(() => saveSiteData({ notes: value }))
  }

  const handleReferencesChange = async (e) => {
    const value = e.target.value
    setReferences(value)
    updateDetailsFieldLocally({ references: value })
    updateReferencessTimeout.schedule(() => saveSiteData({ references: value }))
  }

  const handleChangeContributors = async (e) => {
    const value = e.target.value
    setContributors(value)
    updateDetailsFieldLocally({ consultationWithContributors: value })
    contributorsSaveDebounce.schedule(() => saveSiteData({ consultationWithContributors: value }))
  }

  const handleHabitatChange = async (e) => {
    const value = e.target.value
    setHabitat(value)
    updateDetailsFieldLocally({ habitatDescription: value })
    updateHabitatTimeout.schedule(() => saveSiteData({ habitatDescription: value }))
  }

  const showPaOecmCovered = hasProtectedOrConservedCoverage({
    coveredByPa,
    coveredByOECM,
    paUserUpdated,
    oecmUserUpdated
  })

  return (
    <Container>
      <AssessmentTitle
        titleId='nameAndLocation'
        subTitleId='nameAndLocationDescription'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-name-location-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Input
              value={siteData.siteCode}
              label='siteId'
              placeholder='noPlaceholder'
              infoTooltip='siteId'
              disabled={!canEditPaOecmOverrides}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Input
              value={siteNameNational}
              onChange={handleSiteNationalNameChange}
              label='siteNameNational'
              field='siteNameNational'
              placeholder='nationalSiteName'
              dataTest='form-field'
              infoTooltip='siteNameNationalHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.siteNameNational, 'siteNameNational')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Input
              value={name}
              onChange={handleNameChange}
              label='siteNameInternational'
              field='siteNameEnglish'
              placeholder='internationalSiteName'
              dataTest='form-field'
              infoTooltip='siteNameEnglishHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.name, 'siteNameEnglish')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <MultiSelectionDropdown
              marginTop='16px'
              label='countryLabel'
              field='siteDetailsCountry'
              secondStepOptions={countriesOptions}
              value={CountryCode}
              saveChanges={handleCountryChange}
              infoTooltip='countryHint'
              inputValue={countryInput}
              onInputChange={handleSearchCountry}
              isSearchable={true}
              selectAllOption={true}
              error={getRequiredFieldError(siteProposalFieldValidation.details.SiteDetailsCountryId, 'siteDetailsCountry')}
            />
          </Col>
          <Col md={4}>
            <Input
              value={state}
              onChange={handleStateChange}
              label='stateProvince'
              placeholder='stateProvincePlaceholder'
              dataTest='form-field'
              infoTooltip='stateProvinceHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='siteDetails'
        subTitleId='siteDetailsDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-details-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='siteDescription'
              field='siteDescription'
              value={siteDescription}
              onChange={handleDescriptionChange}
              placeholder='siteDescriptionPlaceholder'
              infoTooltip='siteDescriptionHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.siteDescription, 'siteDescription')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <div
              data-test='sensitive-site-section'
              style={{ width: '100%', marginBottom: '16px' }}
            >
              <Checkbox
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <FormattedMessage id='sensitiveSiteLabel' />
                    <Tooltip
                      style={{ zIndex: 1000 }}
                      zIndex='1070'
                      placement='bottom'
                      trigger={['hover']}
                      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                      overlay={{ label: 'sensitiveSiteLabel', infoTooltip: 'sensitiveSiteHint' }}
                      hyper='https://www.keybiodiversityareas.org'
                      textForLink='www.keybiodiversityareas.org'
                      type='big'
                    >
                      <AssessmentPageSubTittleIconWrapper>
                        <InfoIcon />
                      </AssessmentPageSubTittleIconWrapper>
                    </Tooltip>
                  </span>
                }
                checked={sensitiveSite}
                onChange={handleSensitiveSiteToggle}
                field='sensitiveSite'
                disabled={disabled}
              />
            </div>
            <Textarea
              height='128px'
              label='sensitiveSiteDetailsLabel'
              field='sensitiveSiteDetails'
              value={sensitiveSiteDetails}
              onChange={handleSensitiveSiteDetailsChange}
              placeholder='sensitiveSiteDetailsPlaceholder'
              infoTooltip='sensitiveSiteDetailsHint'
              disabled={disabled || !sensitiveSite}
              zIndex='1070'
              error={
                sensitiveSite &&
                siteProposalFieldValidation.details.sensitiveSiteDetails === false &&
                !String(sensitiveSiteDetails || '').trim()
                  ? {
                    field: 'sensitiveSiteDetails',
                    error: 'sensitiveSiteDetailsRequired'
                  }
                  : { field: '', error: '' }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='siteArea'
              field='siteArea'
              value={siteArea}
              onChange={handleAreaChange}
              onBlur={formatOnBlur(setSiteArea, formatNum2Dp)}
              placeholder='siteAreaPlaceholder'
              dataTest='form-field'
              infoTooltip='siteAreaHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
          <Col md={4}>
            <Input
              label='latitude'
              field='latitudeOfMidPoint'
              value={latitudeOfMidPoint}
              onChange={handleLatitudeChange}
              onBlur={formatOnBlur(setLatitudeOfMidPoint, formatNum4Dp)}
              placeholder='latitudePlaceholder'
              dataTest='form-field'
              infoTooltip='latitudeOfCentroidHint'
              disabled={disabled}
              isOptional={true}
              type='number'
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='longitude'
              field='longitudeOfMidPoint'
              value={longitudeOfMidPoint}
              onChange={handleLongitudeChange}
              onBlur={formatOnBlur(setLongitudeOfMidPoint, formatNum4Dp)}
              placeholder='longitudePlaceholder'
              dataTest='form-field'
              infoTooltip='longitudeOfCentroidHint'
              disabled={disabled}
              isOptional={true}
              type='number'
            />
          </Col>
          <Col md={4}>
            <Input
              label='lowestElevation'
              field='lowestAltitude'
              value={lowestAltitude}
              onChange={handleLowestAltitudeChange}
              onBlur={formatOnBlur(setLowestAltitude, formatNum2Dp)}
              placeholder='lowestElevationPlaceholder'
              dataTest='form-field'
              infoTooltip='lowestElevationHint'
              type='number'
              disabled={disabled}
              isOptional={true}
              error={getAltitudeFieldError(siteProposalFieldValidation.details.lowestAltitude, altitudeError, altitudeRangeError, 'lowestAltitude')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='highestElevation'
              field='highestAltitude'
              value={highestAltitude}
              onChange={handleHighestAltitudeChange}
              onBlur={formatOnBlur(setHighestAltitude, formatNum2Dp)}
              placeholder='highestElevationPlaceholder'
              dataTest='form-field'
              infoTooltip='highestElevationHint'
              type='number'
              disabled={disabled}
              isOptional={true}
              error={getAltitudeFieldError(siteProposalFieldValidation.details.highestAltitude, altitudeError, altitudeRangeError, 'highestAltitude')}
            />
          </Col>
          <Col md={4}>
            <Input
              label='minimumDepth'
              field='minimumDepth'
              value={minimumDepth}
              onChange={handleMinimumDepthChange}
              onBlur={formatOnBlur(setMinimumDepth, formatNum2Dp)}
              placeholder='minimumDepthPlaceholder'
              dataTest='form-field'
              infoTooltip='minimumDepthHint'
              disabled={disabled}
              isOptional={true}
              error={getDepthFieldError(depthError, 'minimumDepth')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='maximumDepth'
              field='maximumDepth'
              value={maximumDepth}
              onChange={handleMaximumDepthChange}
              onBlur={formatOnBlur(setMaximumDepth, formatNum2Dp)}
              placeholder='maximumDepthPlaceholder'
              dataTest='form-field'
              infoTooltip='maximumDepthHint'
              disabled={disabled}
              isOptional={true}
              error={getDepthFieldError(depthError, 'maximumDepth')}
            />
          </Col>
          <Col md={4}>
            <MultiSelectionDropdown
              marginTop='16px'
              label='siteSystemsLabel'
              field='siteSystems'
              secondStepOptions={ecoSystem}
              value={selectedSystems}
              saveChanges={handleSystemsChange}
              infoTooltip='siteSystemsHint'
              placeholder='siteSystemsPlaceholder'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='rationale'
              field='rationaleForSiteInformation'
              value={rationaleForSiteInformation}
              onChange={handleRationaleChange}
              placeholder='rationalePlaceholder'
              infoTooltip='rationaleHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.rationaleForSiteInformation, 'rationaleForSiteInformation')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='biodiversityValues'
              value={additionalBiodiversityValues}
              onChange={handleBiodiversityValuesChange}
              placeholder='biodiversityValuesPlaceholder'
              infoTooltip='biodiversityValueHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='siteValues'
              value={indigenousGroups}
              onChange={handleIndigenousGroupsChange}
              placeholder='siteValuesPlaceholder'
              infoTooltip='siteValueHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='manageabilityDelineation'
        subTitleId='manageabilityDelineationDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-manageability-delineation-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='manageability'
              field='howIsTheSiteManaged'
              value={howIsTheSiteManaged}
              onChange={handleManagedChange}
              placeholder='siteManagedPlaceholder'
              infoTooltip='siteManagedHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.howIsTheSiteManaged, 'howIsTheSiteManaged')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='supersidedSites'
              field='supersidedSites'
              value={supersidedSites}
              onChange={handleSupersidedSitesChange}
              placeholder='supersidedSitesPlaceholder'
              infoTooltip='supersidedSitesHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='delineation'
              field='deliniationRationale'
              value={deliniationRationale}
              onChange={handleDelineationChange}
              placeholder='delineationPlaceholder'
              infoTooltip='delineationHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.deliniationRationale, 'deliniationRationale')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='boundarySourceLabel'
              field='boundary_source'
              value={boundarySource}
              onChange={handleBoundarySourceChange}
              placeholder='boundarySourcePlaceholder'
              infoTooltip='boundarySourceHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='protectedConserved'
        subTitleId='protectedConservedDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-protected-conserved-areas-form'>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='coverageByPa'
              labelTitle='coveredByPa'
              field='coveredByPa'
              value={coveredByPa}
              onChange={handleCoveredByPaChange}
              placeholder='coveredByPaPlaceholder'
              dataTest='form-field'
              infoTooltip='coveredByPaHint'
              readOnly={true}
              disabled={!canEditPaOecmOverrides}
              hyper={'https://www.protectedplanet.net/en/thematic-areas/wdpa?tab=WDPA'}
            />
          </Col>
          <Col md={4}>
            <Input
              label='alternativePaEstimate'
              field='pa_user_updated'
              value={paUserUpdated}
              onChange={handlePaUserUpdatedChange}
              onBlur={formatOnBlur(setPaUserUpdated, formatNum2Dp)}
              placeholder='alternativePaEstimatePlaceholder'
              dataTest='form-field'
              infoTooltip='alternativePaEstimateHint'
              readOnly={!canEditPaOecmOverrides}
              disabled={!canEditPaOecmOverrides}
              isOptional={true}
            />
          </Col>
        </Row>
        {hasAlternativeCoverageValue(paUserUpdated) && (
          <Row>
            <Col md={8} mdOffset={2}>
              <Textarea
                height='96px'
                label='alternativePaJustification'
                field='pa_user_updated_justification'
                value={paUserUpdatedJustification}
                onChange={handlePaUserUpdatedJustificationChange}
                placeholder='alternativePaJustificationPlaceholder'
                infoTooltip='alternativePaJustificationHint'
                disabled={!canEditPaOecmOverrides}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='coverageArea'
              labelTitle='coveredByOECM'
              field='coveredByOECM'
              value={coveredByOECM}
              onChange={handleCoveredByOECMChange}
              placeholder='coveredByOECMPlaceholder'
              dataTest='form-field'
              infoTooltip='coveredByOECMHint'
              readOnly={true}
              disabled={!canEditPaOecmOverrides}
              hyper={'https://www.protectedplanet.net/en/thematic-areas/wdpa?tab=WDPA'}
            />
          </Col>
          <Col md={4}>
            <Input
              label='alternativeOecmEstimate'
              field='oecm_user_updated'
              value={oecmUserUpdated}
              onChange={handleOecmUserUpdatedChange}
              onBlur={formatOnBlur(setOecmUserUpdated, formatNum2Dp)}
              placeholder='alternativeOecmEstimatePlaceholder'
              dataTest='form-field'
              infoTooltip='alternativeOecmEstimateHint'
              readOnly={!canEditPaOecmOverrides}
              disabled={!canEditPaOecmOverrides}
              isOptional={true}
            />
          </Col>
        </Row>
        {hasAlternativeCoverageValue(oecmUserUpdated) && (
          <Row>
            <Col md={8} mdOffset={2}>
              <Textarea
                height='96px'
                label='alternativeOecmJustification'
                field='oecm_user_updated_justification'
                value={oecmUserUpdatedJustification}
                onChange={handleOecmUserUpdatedJustificationChange}
                placeholder='alternativeOecmJustificationPlaceholder'
                infoTooltip='alternativeOecmJustificationHint'
                disabled={!canEditPaOecmOverrides}
              />
            </Col>
          </Row>
        )}
        {showPaOecmCovered && (
          <Row>
            <Col md={8} mdOffset={2}>
              <Textarea
                height='96px'
                label='pasAndOrOecmsCovered'
                field='coveredByWhatProtectedAreas'
                value={coveredByWhatProtectedAreas}
                onChange={handleCoveredByWhatProtectedAreasChange}
                placeholder='pasAndOrOecmsCoveredPlaceholder'
                infoTooltip='pasAndOrOecmsCoveredHint'
                disabled={!canEditPaOecmOverrides}
                error={getRequiredFieldError(
                  siteProposalFieldValidation.details.coveredByWhatProtectedAreas !== false,
                  'coveredByWhatProtectedAreas'
                )}
              />
            </Col>
          </Row>
        )}
      </div>
      <AssessmentTitle
        titleId='habitatsLand'
        subTitleId='habitatsLandDescription'
        marginTop='64px'
        marginBottom='56px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-habitats-land-use-form'>
        <Row>
          <Col md={11} mdOffset={-2}>
            <Habitats site={siteData} disabled={editOnlySiteStatus || disabled} />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='habitatDescription'
              field='habitatDescription'
              value={habitat}
              onChange={handleHabitatChange}
              placeholder='habitatPlaceholder'
              infoTooltip='habitatHint'
              isOptional={true}
              disabled={disabled}
              hyper={'https://www.iucnredlist.org/resources/habitat-classification-scheme'}
              textForLink={'IUCN Habitat Classification Scheme'}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='landUse'
              value={landUseRegimesAtSite}
              onChange={handleLandUseChange}
              placeholder='landUsePlaceholder'
              infoTooltip='landUseHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='pressures'
        subTitleId='pressuresDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-pressures-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='threatsDescription'
              field='threatsDescription'
              value={threatsDescription}
              onChange={handleThreatsDescriptionChange}
              placeholder='threatsDescriptionPlaceholder'
              infoTooltip='threatsDescriptionHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='additionalDetails'
        subTitleId='additionalDetailsDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-additional-details-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='notes'
              value={notes}
              onChange={handleNotesChange}
              placeholder='notesPlaceholder'
              infoTooltip='notesHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='references'
              value={references}
              onChange={handleReferencesChange}
              placeholder='referencesPlaceholder'
              infoTooltip='referencesHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="contributors"
              value={contributors}
              field="contributors"
              infoTooltip="contributorsHint"
              onChange={handleChangeContributors}
              placeholder="contributorsPlaceholder"
              height="128px"
              dataTest="contributors-text-area"
              isOptional={false}
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.consultationWithContributors, 'contributors')}
            />
          </Col>
        </Row>
      </div>
      <ScrollToTop bottom="104px" />
    </Container>
  )
}

export default DetailsPage
